import React, { useState, useRef, useEffect } from 'react';
import { Loader2 } from 'lucide-react';

const PortalGate = ({ token, children }) => {
  const storageKey = `portal_otp_session_${token}`;
  // Admin bypass: if admin password or Supabase auth in sessionStorage, skip OTP
  const adminPw = sessionStorage.getItem('admin_password');
  const supabaseSession = sessionStorage.getItem('sb-auth-token');
  const isAdmin = (adminPw && adminPw.length > 0) || (supabaseSession && supabaseSession.length > 0);

  // Check for email passed from /portal login page
  const urlEmail = new URLSearchParams(window.location.search).get('email') || '';

  const [verified, setVerified] = useState(() => isAdmin || !!sessionStorage.getItem(storageKey));
  const [step, setStep] = useState('email'); // 'email' | 'code'
  const [email, setEmail] = useState(urlEmail);
  const [autoSent, setAutoSent] = useState(false);
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [resendCooldown, setResendCooldown] = useState(0);
  const codeRefs = useRef([]);

  // Auto-send OTP if email was passed from /portal login page
  useEffect(() => {
    if (urlEmail && !verified && !autoSent) {
      setAutoSent(true);
      // Use urlEmail directly to avoid dependency on email state
      const sendOTP = async () => {
        setLoading(true);
        setError(null);
        try {
          const res = await fetch('/api/send-portal-otp', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ token, email: urlEmail.trim() }),
          });
          if (res.status === 429) {
            const json = await res.json();
            setError(json.error || 'Too many requests. Please wait a few minutes.');
            return;
          }
          const json = await res.json();
          if (json.sent) {
            setStep('code');
            setResendCooldown(60);
            setCode(['', '', '', '', '', '']);
          } else {
            setError('Something went wrong. Please try again.');
          }
        } catch {
          setError('Something went wrong. Please try again.');
        } finally {
          setLoading(false);
        }
      };
      sendOTP();
    }
  }, [urlEmail, verified, autoSent, token]);

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  if (verified) return children;

  const handleSendCode = async (e) => {
    if (e) e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/send-portal-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, email: email.trim() }),
      });
      if (res.status === 429) {
        const json = await res.json();
        setError(json.error || 'Too many requests. Please wait a few minutes.');
        return;
      }
      const json = await res.json();
      if (json.sent) {
        setStep('code');
        setResendCooldown(60);
        setCode(['', '', '', '', '', '']);
      } else {
        setError('Something went wrong. Please try again.');
      }
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCodeChange = (index, value) => {
    // Only allow digits
    const digit = value.replace(/\D/g, '').slice(-1);
    const newCode = [...code];
    newCode[index] = digit;
    setCode(newCode);

    // Auto-advance to next input
    if (digit && index < 5) {
      codeRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all 6 digits entered
    if (digit && index === 5) {
      const fullCode = newCode.join('');
      if (fullCode.length === 6) {
        handleVerifyCode(fullCode);
      }
    }
  };

  const handleCodeKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      codeRefs.current[index - 1]?.focus();
    }
  };

  const handleCodePaste = (e) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) {
      e.preventDefault();
      const newCode = pasted.split('');
      setCode(newCode);
      codeRefs.current[5]?.focus();
      handleVerifyCode(pasted);
    }
  };

  const handleVerifyCode = async (fullCode) => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/verify-portal-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, code: fullCode }),
      });
      if (res.status === 429) {
        const json = await res.json();
        setError(json.error || 'Too many attempts. Please wait a few minutes.');
        return;
      }
      const json = await res.json();
      if (json.verified) {
        sessionStorage.setItem(storageKey, json.otp_session);
        setVerified(true);
      } else {
        setError(json.error || 'Invalid code. Please try again.');
        setCode(['', '', '', '', '', '']);
        codeRefs.current[0]?.focus();
      }
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitCode = (e) => {
    e.preventDefault();
    const fullCode = code.join('');
    if (fullCode.length === 6) {
      handleVerifyCode(fullCode);
    }
  };

  const maskedEmail = email ? email.replace(/^(.{2})(.*)(@.*)$/, (_, start, middle, domain) =>
    start + '*'.repeat(Math.max(middle.length, 2)) + domain
  ) : '';

  return (
    <div className="min-h-screen bg-[#0A0A14] flex items-center justify-center px-6">
      <div className="bg-[#18181B] border border-[#F0EFF4]/10 rounded-2xl p-8 w-full max-w-sm">
        <div className="font-bold text-base tracking-tight leading-none mb-6">
          Roof<span className="text-[#7B61FF]">Claw</span>{' '}
          <span className="text-[#F0EFF4]/40 text-xs font-data">PORTAL</span>
        </div>

        {step === 'email' && (
          <>
            <h2 className="font-bold text-[#F0EFF4] text-lg mb-2">Verify Your Identity</h2>
            <p className="text-[#F0EFF4]/60 text-sm mb-6">
              Enter your email to receive a one-time access code.
            </p>
            <form onSubmit={handleSendCode} className="space-y-4">
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="your@email.com"
                required
                className="w-full bg-[#F0EFF4]/5 border border-[#F0EFF4]/10 rounded-xl px-4 py-2.5 text-[#F0EFF4] placeholder-[#F0EFF4]/30 outline-none focus:border-[#7B61FF]/50 text-sm"
              />
              {error && <p className="text-red-400 text-sm">{error}</p>}
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-[#7B61FF] hover:bg-[#6B51EF] disabled:opacity-60 text-[#F0EFF4] font-bold py-2.5 rounded-xl transition-colors text-sm flex items-center justify-center gap-2"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Send Code'}
              </button>
            </form>
          </>
        )}

        {step === 'code' && (
          <>
            <h2 className="font-bold text-[#F0EFF4] text-lg mb-2">Enter Your Code</h2>
            <p className="text-[#F0EFF4]/60 text-sm mb-6">
              We sent a 6-digit code to <span className="text-[#F0EFF4]/80">{maskedEmail}</span>
            </p>
            <form onSubmit={handleSubmitCode} className="space-y-4">
              <div className="flex gap-2 justify-center" onPaste={handleCodePaste}>
                {code.map((digit, i) => (
                  <input
                    key={i}
                    ref={el => codeRefs.current[i] = el}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={e => handleCodeChange(i, e.target.value)}
                    onKeyDown={e => handleCodeKeyDown(i, e)}
                    className="w-11 h-12 bg-[#F0EFF4]/5 border border-[#F0EFF4]/10 rounded-lg text-center text-[#F0EFF4] text-xl font-bold outline-none focus:border-[#7B61FF]/50 transition-colors"
                    autoFocus={i === 0}
                  />
                ))}
              </div>
              {error && <p className="text-red-400 text-sm text-center">{error}</p>}
              <button
                type="submit"
                disabled={loading || code.join('').length < 6}
                className="w-full bg-[#7B61FF] hover:bg-[#6B51EF] disabled:opacity-60 text-[#F0EFF4] font-bold py-2.5 rounded-xl transition-colors text-sm flex items-center justify-center gap-2"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Verify'}
              </button>
            </form>
            <div className="mt-4 text-center">
              <button
                type="button"
                onClick={() => { setStep('email'); setError(null); setEmail(''); }}
                className="text-[#F0EFF4]/40 hover:text-[#F0EFF4]/60 text-xs transition-colors"
              >
                ← Use a different email
              </button>
              {resendCooldown > 0 ? (
                <p className="text-[#F0EFF4]/30 text-xs mt-2">
                  Resend code in {resendCooldown}s
                </p>
              ) : (
                <button
                  type="button"
                  onClick={handleSendCode}
                  disabled={loading}
                  className="block mx-auto text-[#7B61FF]/70 hover:text-[#7B61FF] text-xs mt-2 transition-colors"
                >
                  Resend code
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PortalGate;
